import React from 'react';
import {
  Autocomplete,
  TextField,
  Box,
} from '@mui/material';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import PropTypes from 'prop-types';

const GroupedFieldSelect = ({
  value,
  onChange,
  groupedFields = [],
  placeholder,
  sx = {},
  disabled = false,
  ...props
}) => {
  const flatOptions = React.useMemo(() => {
    return groupedFields.flatMap((group) =>
      group.fields.map((field) => {
        const fieldName = field?.field_name || field?.name || field;
        return {
          label: fieldName,
          value: fieldName,
          group: group.entityName,
          field: field,
        };
      })
    );
  }, [groupedFields]);

  const autocompleteStyles = {
    width: '100%',
    fontFamily: 'IBM Plex Sans, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
    lineHeight: '100%',
    letterSpacing: '-0.02em',
    verticalAlign: 'bottom',
    color: '#383838',
    border: '1px solid #DDDFE3',
    background: '#EFF4FF',
    boxShadow: '0px 4px 10px 0px #0000000D',
    borderRadius: '4px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '4px',
      bgcolor: 'transparent',
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '& .MuiInputBase-input': {
        py: 0.75,
        px: 1.25,
        fontFamily: 'IBM Plex Sans, sans-serif',
        fontWeight: 400,
        fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
        lineHeight: '100%',
        letterSpacing: '-0.02em',
        color: '#383838',
      },
      '& .MuiAutocomplete-endAdornment': {
        '& .MuiSvgIcon-root': {
          color: '#383838',
          transition: 'transform .2s ease',
        },
      },
      '&.Mui-expanded .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
        transform: 'rotate(180deg)',
      },
    },
  };

  const selectedOption = React.useMemo(() => {
    if (!value) return null;
    return flatOptions.find((opt) => opt.value === value) || null;
  }, [value, flatOptions]);

  return (
    <Autocomplete
      size="small"
      options={flatOptions}
      value={selectedOption}
      onChange={(event, newValue) => {
        onChange(event, newValue?.value || null);
      }}
      groupBy={(option) => option.group}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option?.label || option?.value || '';
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof value === 'string') {
          return option.value === value;
        }
        return option.value === value?.value;
      }}
      popupIcon={<KeyboardArrowDownRounded />}
      disablePortal={false}
      disabled={disabled}
      sx={{ ...autocompleteStyles, ...sx }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              height: 'auto',
            },
            '& .MuiInputBase-input::placeholder': {
              color: '#383838',
              opacity: 1,
              fontFamily: 'IBM Plex Sans, sans-serif',
              fontWeight: 400,
              fontSize: { xs: '0.8125rem', sm: '0.875rem', md: '0.9375rem' },
              lineHeight: '100%',
              letterSpacing: '-0.02em',
            },
          }}
        />
      )}
      PaperComponent={({ children, ...other }) => (
        <Box
          component="div"
          {...other}
          sx={{
            borderRadius: '4px',
            border: '1px solid #E6E6E6',
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
            mt: 0.5,
            overflow: 'visible',
            backgroundColor: '#FFFFFF',
            zIndex: 10001,
            position: 'relative',
            display: 'block',
          }}
        >
          {children}
        </Box>
      )}
      ListboxProps={{
        sx: {
          py: 0.5,
          maxHeight: '300px',
          overflow: 'auto',
          '& .MuiAutocomplete-groupLabel': {
            fontSize: '0.75rem',
            fontWeight: 600,
            color: '#383838',
            px: 1.25,
            py: 0.5,
            bgcolor: '#f5f5f5',
            borderBottom: '1px solid #e0e0e0',
          },
          '& .MuiAutocomplete-option': {
            fontSize: '0.875rem',
            px: 1.25,
            py: 0.75,
            pl: 2.5,
            transition: 'background-color .15s ease',
            '&:hover': { backgroundColor: '#DDF3FA' },
            '&.Mui-focused': { backgroundColor: '#DDF3FA' },
          },
        },
      }}
      componentsProps={{
        popper: {
          style: { zIndex: 10001 },
          placement: 'bottom-start',
          disablePortal: false,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 4],
              },
            },
          ],
        },
      }}
      PopperProps={{
        style: { zIndex: 10001 },
        placement: 'bottom-start',
        disablePortal: false,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ],
        ...props.PopperProps,
      }}
      {...props}
    />
  );
};

GroupedFieldSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  groupedFields: PropTypes.arrayOf(
    PropTypes.shape({
      entityName: PropTypes.string.isRequired,
      fields: PropTypes.array.isRequired,
    })
  ),
  placeholder: PropTypes.string,
  sx: PropTypes.object,
  disabled: PropTypes.bool,
  PopperProps: PropTypes.object,
};

export default GroupedFieldSelect;
